/**
 * The four baseline states every screen must define. UF_00 section 6.5.
 *
 *   Empty     gives a helpful FIRST ACTION, not an apology
 *   Loading   skeletons, NEVER a blank screen
 *   Error     specific and RECOVERABLE
 *   Blocked   explains the gate and the NEXT STEP  (see gate.tsx and chrome.tsx)
 *
 * They are built once, here, so that a panel written next month cannot invent a fifth way of saying
 * "nothing here yet" — and so that "we could not load your wallet" never appears as a blank card that
 * a user stares at wondering whether their money is gone.
 */

/**
 * A skeleton. NOT a spinner.
 *
 * A spinner says "something is happening". A skeleton says "something is happening, and here is the
 * shape of what is coming" — which for a balance means the user can see the app is loading their
 * money rather than fearing it has lost it. On a slow Kenyan mobile connection that difference is
 * most of the perceived quality of the product.
 */
export function Skeleton({ className = '' }: { className?: string }) {
  return <span className={`block animate-pulse rounded bg-border ${className}`} aria-hidden />;
}

export function LoadingCard({ lines = 3 }: { lines?: number }) {
  return (
    <div
      className="rounded-card border border-border bg-surface p-5"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <span className="sr-only">Loading</span>
      <Skeleton className="h-4 w-24" />
      <Skeleton className="mt-3 h-8 w-40" />
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className="mt-2.5 h-3 w-full" />
      ))}
    </div>
  );
}

/**
 * Empty. It gives the user something to DO.
 *
 * "No transactions yet" is a dead end. "No transactions yet — add money to get started" is the first
 * step of the product, and an empty state is the screen a new user sees most.
 */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: { label: string; onClick?: () => void; href?: string };
}) {
  return (
    <div className="rounded-card border border-dashed border-border bg-surface p-8 text-center">
      <h3 className="font-medium text-ink">{title}</h3>
      <p className="mx-auto mt-1 max-w-xs text-sm text-ink-muted">{body}</p>

      {action ? (
        action.href ? (
          <a
            href={action.href}
            className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
          >
            {action.label}
          </a>
        ) : (
          <button
            onClick={action.onClick}
            className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
          >
            {action.label}
          </button>
        )
      ) : null}
    </div>
  );
}

/**
 * Error. Specific, and RECOVERABLE.
 *
 * The retry button is the whole point. An error with no way out is a dead app, and the most common
 * cause of one of these is a network that dropped for two seconds.
 *
 * Note what it does NOT say: it does not report that money moved or did not move. That claim belongs
 * to `BlockedNotice`, which is shown for a REFUSAL, where the backend has actually told us nothing
 * moved. Saying "nothing has moved" here — where the failure might have been the response getting
 * lost on the way back from a successful payment — would be a comforting lie.
 */
export function ErrorState({
  title = 'We could not load this',
  body,
  onRetry,
}: {
  title?: string;
  body: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="rounded-card border border-danger/30 bg-danger/5 p-5 text-center text-sm"
    >
      <h3 className="font-medium text-danger">{title}</h3>
      <p className="mx-auto mt-1 max-w-sm text-ink-muted">{body}</p>

      {onRetry ? (
        <button
          onClick={onRetry}
          className="mt-4 inline-flex rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-ink transition hover:bg-canvas"
        >
          Try again
        </button>
      ) : null}
    </div>
  );
}
